import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;
const JIG_TYPES = ['Assembly Fixture','Test Fixture','Calibration Jig','Inspection Jig','Programming Jig','Soldering Fixture','Packaging Jig','Generic'];
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const SUPPLIER_LABELS = { gpv:'GPV', ubiqconn:'Ubiqconn', acmepoint:'Acmepoint', hk_ringer:'HK Ringer' };

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

export default function JigNew() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const supplierKey = SUPPLIERS.includes(role.replace(/^test-/,'')) ? role.replace(/^test-/,'') : null;
  const canEdit = ['admin','quality-manager','production-engineer','service'].includes(role) || !!supplierKey;

  const [docNum, setDocNum] = useState('');
  const [form, setForm] = useState({
    name:'', type:'Generic', description:'',
    applicableProducts:[], applicableStationTypes:[],
    dimensions:{ width:'', depth:'', height:'', unit:'mm' },
    material:'', partNumber:'', manufacturer:'', notes:'',
    // Physical identity, design control & custody
    assetId:'', serialNumber:'', drawingNumber:'', drawingRevision:'',
    location:'', ownerOrg:'', responsiblePerson:'', inServiceDate:'',
    // Periodic verification + maintenance schedule (logged on the detail page)
    verificationRequired:false, verificationIntervalDays:'', verificationMethod:'', verificationStandard:'',
    maintenanceRequired:false, maintenanceIntervalDays:'',
    msaRequired:false,
    visibility:{ suppliers:[], supplierAccess:'None' },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canEdit) { navigate('/quality/jigs'); return; }
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/next-doc-number?prefix=JG-NIN-`, {
        headers:{ Authorization:`Bearer ${token}` },
      }).then(r=>r.json()).then(d=>{ if (d.ok) setDocNum(d.documentNumber); })
    );
  }, []);// eslint-disable-line

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const setDim = (k,v) => setForm(f=>({...f,dimensions:{...f.dimensions,[k]:v}}));
  const toggleSupplier = s => {
    const cur = form.visibility.suppliers;
    const next = cur.includes(s) ? cur.filter(x=>x!==s) : [...cur,s];
    setForm(f=>({...f,visibility:{...f.visibility,suppliers:next,supplierAccess:next.length?'Specific':'None'}}));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/jigs`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ ...form, documentNumber: docNum }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error||'Failed');
      navigate(`/quality/jigs/${data.jig._id}`);
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const label = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };

  return (
    <div style={{ maxWidth:700 }}>
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:24 }}>
        <button onClick={()=>navigate('/quality/jigs')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>New Jig Template</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
          <div>
            <label style={label}>Document Number</label>
            <input value={docNum} readOnly style={{ ...inp, opacity:0.6, cursor:'not-allowed' }} />
          </div>
          <div>
            <label style={label}>Type</label>
            <select value={form.type} onChange={e=>set('type',e.target.value)} style={inp}>
              {JIG_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={label}>Name *</label>
          <input value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. PCB Assembly Fixture v3"
            required style={inp} />
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={label}>Description</label>
          <textarea value={form.description} onChange={e=>set('description',e.target.value)}
            rows={3} style={{ ...inp, resize:'vertical' }} placeholder="Purpose and usage notes" />
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
          <div>
            <label style={label}>Manufacturer</label>
            <input value={form.manufacturer} onChange={e=>set('manufacturer',e.target.value)} style={inp} />
          </div>
          <div>
            <label style={label}>Part Number</label>
            <input value={form.partNumber} onChange={e=>set('partNumber',e.target.value)} style={inp} />
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={label}>Material</label>
          <input value={form.material} onChange={e=>set('material',e.target.value)}
            placeholder="e.g. Aluminium 6061" style={inp} />
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={label}>Dimensions</label>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 80px',gap:8 }}>
            {['width','depth','height'].map(d=>(
              <input key={d} type="number" placeholder={d} value={form.dimensions[d]}
                onChange={e=>setDim(d,e.target.value)} style={inp} />
            ))}
            <select value={form.dimensions.unit} onChange={e=>setDim('unit',e.target.value)} style={inp}>
              {['mm','cm','m','inch'].map(u=><option key={u}>{u}</option>)}
            </select>
          </div>
        </div>

        {/* Asset & identity — documentNumber is the QMS record; these identify the
            physical fixture, its drawing and who is accountable for it. */}
        <div style={{ borderTop:'1px solid var(--ni-border-soft)',margin:'4px 0 16px',paddingTop:16 }}>
          <div style={{ ...label, color:'#a78bfa', marginBottom:12 }}>Asset &amp; Identity</div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12 }}>
            {[['assetId','Asset ID / Tag'],['serialNumber','Serial Number'],['location','Location'],
              ['drawingNumber','Drawing No.'],['drawingRevision','Drawing Rev.'],['ownerOrg','Owner Org'],
              ['responsiblePerson','Responsible Person']].map(([k,lbl])=>(
              <div key={k}><label style={label}>{lbl}</label>
                <input value={form[k]} onChange={e=>set(k,e.target.value)} style={inp} /></div>
            ))}
            <div><label style={label}>In-Service Date</label>
              <input type="date" value={form.inServiceDate} onChange={e=>set('inServiceDate',e.target.value)} style={inp} /></div>
          </div>
        </div>

        {/* Verification & maintenance schedule — the actual log events are recorded
            on the detail page once the jig exists. */}
        <div style={{ borderTop:'1px solid var(--ni-border-soft)',margin:'4px 0 16px',paddingTop:16 }}>
          <div style={{ ...label, color:'#a78bfa', marginBottom:12 }}>Verification &amp; Maintenance</div>
          <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',marginBottom:10 }}>
            <input type="checkbox" checked={form.verificationRequired}
              onChange={e=>set('verificationRequired',e.target.checked)} />
            Periodic verification required
          </label>
          {form.verificationRequired && (
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12 }}>
              <div><label style={label}>Interval (days)</label>
                <input type="number" value={form.verificationIntervalDays} onChange={e=>set('verificationIntervalDays',e.target.value)} style={inp} /></div>
              <div><label style={label}>Method</label>
                <input value={form.verificationMethod} onChange={e=>set('verificationMethod',e.target.value)} placeholder="e.g. CMM, go/no-go" style={inp} /></div>
              <div><label style={label}>Standard</label>
                <input value={form.verificationStandard} onChange={e=>set('verificationStandard',e.target.value)} style={inp} /></div>
            </div>
          )}
          <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',marginBottom:10 }}>
            <input type="checkbox" checked={form.maintenanceRequired}
              onChange={e=>set('maintenanceRequired',e.target.checked)} />
            Scheduled maintenance required
          </label>
          {form.maintenanceRequired && (
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12 }}>
              <div><label style={label}>Interval (days)</label>
                <input type="number" value={form.maintenanceIntervalDays} onChange={e=>set('maintenanceIntervalDays',e.target.value)} style={inp} /></div>
            </div>
          )}
          <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer' }}>
            <input type="checkbox" checked={form.msaRequired}
              onChange={e=>set('msaRequired',e.target.checked)} />
            Measurement Systems Analysis (MSA) required <span style={{ color:'var(--ni-text-muted)',fontSize:12 }}>— for gauging fixtures</span>
          </label>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={label}>Notes</label>
          <textarea value={form.notes} onChange={e=>set('notes',e.target.value)}
            rows={2} style={{ ...inp, resize:'vertical' }} />
        </div>

        <div style={{ marginBottom:24 }}>
          <label style={label}>Supplier Visibility</label>
          {supplierKey ? (
            <div style={{ fontSize:13,color:'var(--ni-text-muted)',background:'#a78bfa11',
              border:'1px solid #a78bfa33',borderRadius:8,padding:'10px 12px',lineHeight:1.5 }}>
              🔒 Visible to North Invent and your organisation (<strong style={{ color:'#a78bfa' }}>{SUPPLIER_LABELS[supplierKey]}</strong>).
              North Invent reviews and releases your contribution — visibility is set automatically.
            </div>
          ) : (
            <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
              {SUPPLIERS.map(s=>(
                <button type="button" key={s}
                  onClick={()=>toggleSupplier(s)}
                  style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,
                    cursor:'pointer',border:'1px solid',
                    background: form.visibility.suppliers.includes(s) ? '#a78bfa22':'transparent',
                    color: form.visibility.suppliers.includes(s) ? '#a78bfa':'var(--ni-text-muted)',
                    borderColor: form.visibility.suppliers.includes(s) ? '#a78bfa':'var(--ni-border-soft)' }}>
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && <div style={{ color:'#EC2124',fontSize:13,marginBottom:12 }}>{error}</div>}
        <div style={{ display:'flex',gap:10 }}>
          <button type="submit" disabled={saving}
            style={{ background:'#a78bfa',color:'#000',border:'none',borderRadius:6,
              padding:'10px 24px',fontWeight:700,fontSize:13,cursor:saving?'wait':'pointer' }}>
            {saving?'Creating…':'Create Jig Template'}
          </button>
          <button type="button" onClick={()=>navigate('/quality/jigs')}
            style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
              borderRadius:6,padding:'10px 20px',fontSize:13,cursor:'pointer' }}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
